import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { debounce, startCase, toLower } from 'lodash';
import { AxiosError } from 'axios';
import { DefaultOptionType } from 'antd/lib/select';
import { Divider, Drawer, DrawerProps, Form, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import { newTeamMemberValidationSchema } from 'views/teams/validation-schemas';
import { ALLOWED_NEW_MEMBER_ROLES, NewTeamMemberFormValues } from 'views/teams/types';
import useIndividuals from 'hooks/useIndividuals';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useTeams from 'hooks/useTeams';
import { createIndividual } from 'store/individuals/client';

const initialValues: NewTeamMemberFormValues = {
    assignee: null,
    email: '',
    name: '',
    role: null,
};

interface Props extends DrawerProps {
    customerId: string | undefined;
    onClose: () => void;
    onSuccess: () => void;
}

const NewTeamMemberFormDrawer = ({ customerId, onClose, onSuccess, ...rest }: Props) => {
    const { teamId } = useParams();
    const { team, addTeamMember } = useTeams(teamId);
    const { individuals, listIndividuals } = useIndividuals();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [assigneeInputValue, setAssigneeInputValue] = useState('');
    const [showNewTeamMemberSection, setShowNewTeamMemberSection] = useState(false);

    const formik = useFormik({
        initialValues,
        validationSchema: newTeamMemberValidationSchema,
        onSubmit: async (values, helpers) => {
            const isAssigning = Boolean(values.assignee);

            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });

                if (isAssigning) {
                    if (!team?.id || !values.assignee) {
                        throw new Error('Not enough data');
                    }

                    const selectedIndividual = individuals.list.find((i) => i.id === values.assignee);
                    const individualRole = selectedIndividual?.type;

                    if (!selectedIndividual || !individualRole) {
                        throw new Error('Missing user data. Please try again.');
                    }

                    await addTeamMember({
                        teamId: team.id,
                        individualId: values.assignee,
                        role: individualRole,
                    });
                } else {
                    if (!values.role || !values.name || !values.email) {
                        throw new Error('No role selected.');
                    }

                    await createIndividual({
                        fullName: values.name,
                        email: values.email,
                        type: values.role,
                        teamId,
                        customerId,
                        isInvited: true,
                    });
                }

                Notification({
                    placement: 'top',
                    type: 'success',
                    messageTitle: 'Success',
                    description: 'Successfully assigned team member',
                });

                onSuccess();
                setAssigneeInputValue('');
                helpers.resetForm();
            } catch (error) {
                const errorInfo = error instanceof AxiosError && error?.response?.data?.message;
                Notification({
                    placement: 'top',
                    type: 'error',
                    messageTitle: 'Error',
                    description: errorInfo || 'Could not add team meber. Please try again.',
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleAddTeamMember = () => {
        formik.handleSubmit();
    };

    const handleSearchIndividuals = (val: string) => {
        setAssigneeInputValue(val);
        if (formik.values.assignee) {
            formik.setFieldValue('assignee', undefined);
            formik.setFieldTouched('assignee', true, false);
        }
        debouncedSearchIndividuals(val);
    };

    const handleSelectAssignee = (val: string, option?: DefaultOptionType) => {
        formik.setFieldValue('assignee', val);
        setAssigneeInputValue(String(option?.label ?? ''));
        debouncedSearchIndividuals.cancel();
    };

    const handleClearAssignee = () => {
        setAssigneeInputValue('');
        formik.setFieldValue('assignee', undefined);
    };

    const handleCloseDrawer = () => {
        formik.resetForm();
        onClose();
        setShowNewTeamMemberSection(false);
    };

    const debouncedSearchIndividuals = useMemo(
        () => debounce((q: string) => listIndividuals({ query: q }), 300),
        [listIndividuals]
    );

    // Filter out individuals that are already in this team
    const individualsOptions: DefaultOptionType[] = useMemo(
        () =>
            individuals.list.map((i) => ({
                label: i.fullName,
                value: i.id,
            })) ?? [],
        [individuals.list]
    );

    const newTeamMemberSection = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <label>
                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                Name
                <FormInput
                    placeholder="Enter member name"
                    fieldProps={formik.getFieldProps('name')}
                    fieldMeta={formik.getFieldMeta('name')}
                    fieldHelper={formik.getFieldHelpers('name')}
                />
            </label>
            <label>
                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                Email
                <FormInput
                    placeholder="Enter email"
                    fieldProps={formik.getFieldProps('email')}
                    fieldMeta={formik.getFieldMeta('email')}
                    fieldHelper={formik.getFieldHelpers('email')}
                />
            </label>
            <label>
                <LabelRequiredSymbol style={{ marginRight: 8 }} />
                Role
                <FormSelect
                    hideKeys
                    placeholder="Select role"
                    fieldHelper={formik.getFieldHelpers('role')}
                    fieldMeta={formik.getFieldMeta('role')}
                    fieldProps={formik.getFieldProps('role')}
                    selectOptions={ALLOWED_NEW_MEMBER_ROLES.map((role) => ({
                        id: role,
                        value: startCase(toLower(role)),
                    }))}
                />
            </label>
        </Space>
    );

    // Get initial individuals results
    useEffect(() => {
        listIndividuals();
    }, [listIndividuals]);

    return (
        <Drawer
            open={true}
            title="New Member"
            maskClosable={false}
            onClose={handleCloseDrawer}
            width={500}
            extra={
                <Space>
                    <Button onClick={handleCloseDrawer}>Cancel</Button>
                    <Button
                        type="primary"
                        disabled={!formik.isValid}
                        loading={formik.isSubmitting}
                        onClick={handleAddTeamMember}
                    >
                        Create
                    </Button>
                </Space>
            }
            {...rest}
        >
            <form onSubmit={formik.handleSubmit}>
                <Form layout="vertical">
                    <FormAutocomplete
                        required
                        disabled // Disabled as we currently can not assign because of BE restrictions
                        style={{ width: '100%' }}
                        label="Assign"
                        inputPlaceholder="Assign team member"
                        fieldMeta={formik.getFieldMeta('assignee')}
                        onBlur={() => formik.setFieldTouched('assignee', true)}
                        onSearch={handleSearchIndividuals}
                        onSelect={handleSelectAssignee}
                        onClear={handleClearAssignee}
                        options={individualsOptions}
                        value={assigneeInputValue}
                    />

                    <Divider orientation="left">or</Divider>
                    {showNewTeamMemberSection ? (
                        newTeamMemberSection
                    ) : (
                        <Button
                            onClick={() => {
                                setShowNewTeamMemberSection(true);
                            }}
                            style={{ color: 'var(--brand-6)', marginLeft: 16 }}
                            type="link"
                            icon={<PlusOutlined />}
                        >
                            Create New Member
                        </Button>
                    )}
                </Form>
            </form>
        </Drawer>
    );
};

export default NewTeamMemberFormDrawer;
